import React from 'react';
import {
  Section, Stat, Tableau, Clic, FicheLien, Statut, fmtMt, fmtNb, fmtDH, fmtL, fmtH, fmtDateFr,
} from './components/commun';
import { KpiDelta } from './components/Indicateurs';
import { HistogrammeJour } from './components/Graphiques';

// Onglets du dashboard technique. Chaque chiffre cliquable ouvre une liste (onListe), chaque nom ouvre une fiche.
// props communes : data (réponse de l'onglet), onListe(kind, titre, query), periode { start, end }

const dec = (v) => (v == null ? '—' : String(v).replace('.', ','));
const lienChantier = (periode) => ({
  key: 'chantier', label: 'Chantier',
  render: r => <FicheLien type="chantier" id={r.chantierId} periode={periode}>{r.chantier || 'Sans chantier'}</FicheLien>,
});
const lienMateriel = (periode) => ({
  key: 'matricule', label: 'Matricule',
  render: r => <FicheLien type="materiel" id={r.materielId} periode={periode}>{r.matricule || `#${r.materielId}`}</FicheLien>,
});
// Cellule chiffre cliquable → liste filtrée sur la ligne (chantier) ; ligne de total (pas de chantierId) → liste complète
const cellule = (onListe, kind, titre, fmt, query) => (v, r) => {
  if (!v) return <span className="text-muted">—</span>;
  const ligne = r && 'chantierId' in r;
  return (
    <Clic onClick={() => onListe(kind, ligne ? `${titre} — ${r.chantier || 'Sans chantier'}` : titre,
      ligne ? { ...query, chantierId: r.chantierId } : query)}>{fmt(v)}</Clic>
  );
};

/* ─── Vue d'ensemble ───────────────────────────────────────────────────────── */
export const VueEnsemble = ({ data, onListe, periode, comparer }) => {
  const k = data.kpis, kp = data.kpisPrecedents, s = data.serie;
  const serie = (key) => s.map(d => d[key]);
  return (
    <>
      <div className="row">
        <KpiDelta titre="Effectif / jour" valeur={fmtNb(k.effectifJour)} actuel={k.effectifJour} precedent={kp.effectifJour}
          serie={serie('ouvriers')} couleur="#2e7d32" comparer={comparer} onClick={() => onListe('ouvriers', 'Ouvriers pointés sur la période')}
          info={`Lignes de pointage / jours saisis. ${fmtNb(k.ouvriers)} ouvriers différents sur la période.`} />
        <KpiDelta titre="Masse salariale" valeur={fmtDH(k.masse)} actuel={k.masse} precedent={kp.masse}
          serie={serie('masse')} couleur="#6d4e36" comparer={comparer} onClick={() => onListe('ouvriers', 'Masse salariale par ouvrier')}
          info="Taux appliqué au pointage × (jours + heures sup / 10), comme la paie." />
        <KpiDelta titre="Heures matériel" valeur={fmtH(k.heuresMateriel)} actuel={k.heuresMateriel} precedent={kp.heuresMateriel}
          serie={serie('heuresMateriel')} couleur="#1565c0" comparer={comparer} onClick={() => onListe('materiels', 'Heures matériel par matériel')}
          info={`Heures réelles des pointages matériel — ${fmtNb(k.materiels)} matériels pointés.`} />
        <KpiDelta titre="Gasoil" valeur={fmtL(k.litres)} actuel={k.litres} precedent={kp.litres} sensInverse
          serie={serie('litres')} couleur="#e65100" comparer={comparer} onClick={() => onListe('materiels', 'Gasoil par matériel')}
          info="Litres saisis dans les pointages matériel." />
        <KpiDelta titre="Réparations" valeur={fmtDH(k.reparations)} actuel={k.reparations} precedent={kp.reparations} sensInverse
          couleur="#6a1b9a" comparer={comparer} onClick={() => onListe('reparations', 'Réparations de la période')}
          info={`${fmtNb(k.reparationsLignes)} ligne(s) de réparation (date du BDC dans la période, qté × prix).`} />
      </div>
      {comparer && (
        <div className="small text-muted mb-3">
          Comparaison avec la période précédente de même durée : {fmtDateFr(data.periode.prevStart)} → {fmtDateFr(data.periode.prevEnd)}.
          Une saisie en retard sur la période en cours fait baisser les chiffres.
        </div>
      )}

      <Section titre="🏗 Coût par chantier" info="Main-d'œuvre, heures et gasoil du matériel pointé sur le chantier, réparations des matériels affectés au chantier. Cliquez sur un chiffre pour la liste, sur un chantier pour sa fiche.">
        <Tableau rows={data.chantiers} total triInitial={{ key: 'masse', dir: 'desc' }} columns={[
          lienChantier(periode),
          { key: 'societe', label: 'Société' },
          { key: 'ouvriersJour', label: 'Ouvriers / j', num: true, fmt: cellule(onListe, 'ouvriers', 'Ouvriers', fmtNb) },
          { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb },
          { key: 'heuresSup', label: 'Heures sup', num: true, total: true, fmt: fmtNb },
          { key: 'masse', label: 'Masse (DH)', num: true, total: true, fmt: cellule(onListe, 'ouvriers', 'Masse salariale', fmtMt) },
          { key: 'heuresMateriel', label: 'Heures mat.', num: true, total: true, fmt: cellule(onListe, 'materiels', 'Heures matériel', fmtNb) },
          { key: 'litres', label: 'Gasoil (L)', num: true, total: true, fmt: cellule(onListe, 'materiels', 'Gasoil', fmtNb) },
          { key: 'reparations', label: 'Réparations (DH)', num: true, total: true, fmt: cellule(onListe, 'reparations', 'Réparations', fmtMt) },
        ]} />
      </Section>

      <Section titre="📈 Évolution journalière">
        <HistogrammeJour data={s} series={[
          { key: 'ouvriers', label: 'Ouvriers pointés', couleur: '#2e7d32', unite: 'pointages' },
          { key: 'masse', label: 'Masse salariale', couleur: '#6d4e36', unite: 'DH' },
          { key: 'heuresMateriel', label: 'Heures matériel', couleur: '#1565c0', unite: 'h' },
          { key: 'litres', label: 'Gasoil', couleur: '#e65100', unite: 'L' },
        ]} />
      </Section>
    </>
  );
};

/* ─── Main-d'œuvre ─────────────────────────────────────────────────────────── */
export const MainOeuvre = ({ data, onListe, periode }) => {
  const tot = (key) => data.parChantier.reduce((s, r) => s + r[key], 0);
  return (
    <>
      <div className="row">
        <Stat label="Jours pointés" value={fmtNb(tot('jours'))} sub={`${fmtNb(tot('heuresSup'))} h sup`}
          onClick={() => onListe('ouvriers', 'Ouvriers pointés sur la période')} />
        <Stat label="Masse salariale" value={fmtDH(tot('masse'))} onClick={() => onListe('ouvriers', 'Masse salariale par ouvrier')}
          info="Taux appliqué au pointage × (jours + heures sup / 10)." />
        <Stat label={`Chantiers non pointés le ${fmtDateFr(data.jourPointage).slice(0, 5)}`} value={`${fmtNb(data.chantiersSansPointage)} / ${fmtNb(data.chantiersActifs)}`}
          alert={data.chantiersSansPointage > 0} onClick={data.chantiersSansPointage ? () => onListe('chantiersSansPointage', 'Chantiers actifs non pointés au dernier jour saisi') : null}
          info="Chantiers avec pointage sur les 30 derniers jours mais aucun le dernier jour saisi (saisie en retard ?)." />
        <Stat label="Doublons de pointage" value={fmtNb(data.doublons)} alert={data.doublons > 0}
          onClick={data.doublons ? () => onListe('doublons', 'Doublons de pointage') : null}
          info="Ouvrier pointé plusieurs fois le même jour, ou plus d'un jour au total sur une date." />
      </div>
      <Section titre="🏗 Par chantier">
        <Tableau rows={data.parChantier} total triInitial={{ key: 'masse', dir: 'desc' }} columns={[
          lienChantier(periode), { key: 'societe', label: 'Société' },
          { key: 'ouvriers', label: 'Ouvriers', num: true, fmt: cellule(onListe, 'ouvriers', 'Ouvriers', fmtNb) },
          { key: 'ouvriersJour', label: 'Ouvriers / j', num: true },
          { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb },
          { key: 'heuresSup', label: 'Heures sup', num: true, total: true, fmt: fmtNb },
          { key: 'masse', label: 'Masse (DH)', num: true, total: true, fmt: fmtMt },
        ]} />
      </Section>
      <Section titre="👷 Par qualification">
        <Tableau rows={data.parQualification} total triInitial={{ key: 'masse', dir: 'desc' }} columns={[
          { key: 'qualification', label: 'Qualification' },
          { key: 'ouvriers', label: 'Ouvriers', num: true, fmt: fmtNb },
          { key: 'jours', label: 'Jours', num: true, total: true, fmt: fmtNb },
          { key: 'heuresSup', label: 'Heures sup', num: true, total: true, fmt: fmtNb },
          { key: 'masse', label: 'Masse (DH)', num: true, total: true, fmt: fmtMt },
        ]} />
      </Section>
    </>
  );
};

/* ─── Matériel & gasoil ────────────────────────────────────────────────────── */
export const Materiel = ({ data, onListe, periode }) => {
  const taux = data.parcActif ? Math.round((data.pointes * 100) / data.parcActif) : null;
  return (
    <>
      <div className="row">
        <Stat label="Utilisation" value={`${taux ?? '—'} %`} sub={`${fmtNb(data.pointes)} pointés / ${fmtNb(data.parcActif)} en parc actif`}
          onClick={() => onListe('materiels', 'Matériels pointés sur la période')}
          info="Parc actif = matériels non bloqués pointés au moins une fois sur 90 jours." />
        <Stat label="Inactifs > 7 j" value={fmtNb(data.inactifs)} alert={data.inactifs > 0}
          onClick={data.inactifs ? () => onListe('inactifs', 'Matériels du parc actif sans pointage depuis plus de 7 jours') : null}
          info="Matériels du parc actif sans pointage depuis plus de 7 jours (immobilisés, en panne ou non saisis)." />
        <Stat label="Conso anormale" value={fmtNb(data.anomalies)} alert={data.anomalies > 0}
          onClick={data.anomalies ? () => onListe('anomalies', 'Consommations de gasoil anormales') : null}
          info={`L/h supérieur à ${data.seuils.anomalieRatio} × la moyenne du même type (au moins ${data.seuils.anomalieHeuresMin} h sur la période).`} />
        <Stat label="Écarts compteur" value={fmtNb(data.ecarts)} alert={data.ecarts > 0}
          onClick={data.ecarts ? () => onListe('ecarts', 'Écarts compteur suspects') : null}
          info="Pointages signalés « écart suspect » entre compteur et heures déclarées." />
        <Stat label="Bloqués (hors parc)" value={fmtNb(data.bloques)} sub={`${fmtNb(data.enPanne)} en panne / maintenance`} />
      </div>
      <Section titre="🚜 Activité par matériel" info="Heures, gasoil et consommation L/h sur la période ; comparaison avec la moyenne du même type.">
        <Tableau rows={data.parMateriel} total maxHeight={520} triInitial={{ key: 'litres', dir: 'desc' }} columns={[
          lienMateriel(periode), { key: 'type', label: 'Type' }, lienChantier(periode),
          { key: 'joursPointes', label: 'Jours', num: true },
          { key: 'heures', label: 'Heures', num: true, total: true, fmt: fmtNb },
          { key: 'litres', label: 'Gasoil (L)', num: true, total: true, fmt: fmtNb },
          { key: 'litresHeure', label: 'L/h', num: true, fmt: (v, r) => <span style={{ color: r.anomalie ? '#c62828' : undefined, fontWeight: r.anomalie ? 700 : undefined }}>{dec(v)}{r.anomalie ? ' ⚠️' : ''}</span> },
          { key: 'moyenneType', label: 'Moy. type', num: true, fmt: dec },
          { key: 'ecarts', label: 'Écarts', num: true, fmt: v => (v ? <span style={{ color: '#c62828' }}>{v}</span> : '') },
        ]} />
      </Section>
    </>
  );
};

/* ─── Entretien ────────────────────────────────────────────────────────────── */
const PAPIERS = [['visite_technique', 'VT'], ['assurance', 'Assurance'], ['vignette', 'Vignette'], ['feuille_circulation', 'Feuille circ.'], ['mouchard', 'Mouchard']];
const celluleEcheance = (e) => {
  if (!e?.date) return <span className="text-muted">—</span>;
  const c = e.statut === 'DEPASSE' ? '#b71c1c' : e.statut === 'ALERTE' ? '#c87800' : '#2e7d32';
  return <span style={{ color: c, whiteSpace: 'nowrap' }} title={fmtDateFr(e.date)}>{e.jours < 0 ? `−${-e.jours} j` : `${e.jours} j`}</span>;
};

export const Entretien = ({ data, onListe, periode }) => {
  const v = data.vidanges, p = data.papiers, r = data.reparations;
  return (
    <>
      <div className="row">
        <Stat label="Vidanges dépassées" value={fmtNb(v.depasse)} alert={v.depasse > 0} sub={`sur ${fmtNb(v.suivis)} matériels suivis`}
          onClick={v.depasse ? () => onListe('vidanges', 'Vidanges dépassées', { niveau: 'DEPASSE' }) : null} />
        <Stat label="Vidanges critiques" value={fmtNb(v.critique)} onClick={v.critique ? () => onListe('vidanges', 'Vidanges critiques', { niveau: 'CRITIQUE' }) : null} />
        <Stat label="Vidanges en alerte" value={fmtNb(v.alerte)} onClick={v.alerte ? () => onListe('vidanges', 'Vidanges en alerte', { niveau: 'ALERTE' }) : null} />
        <Stat label="Papiers expirés" value={fmtNb(p.expires)} alert={p.expires > 0} sub={`sur ${fmtNb(p.vehicules)} véhicules`}
          onClick={p.expires ? () => onListe('papiers', 'Véhicules avec un papier expiré', { statut: 'DEPASSE' }) : null}
          info="Visite technique, assurance, vignette, feuille de circulation ou mouchard dont l'échéance est passée." />
        <Stat label="À renouveler ≤ 30 j" value={fmtNb(p.alerte)} onClick={p.alerte ? () => onListe('papiers', 'Papiers à renouveler sous 30 jours', { statut: 'ALERTE' }) : null} />
        <Stat label="Réparations" value={fmtDH(r.montant)} sub={`${fmtNb(r.lignes)} ligne(s)`} onClick={r.lignes ? () => onListe('reparations', 'Réparations de la période') : null} />
      </div>
      <div className="row">
        <div className="col-12 col-xl-6">
          <Section titre="🛢 Vidanges à faire" info="Matériels des sociétés suivies (Rutas, Pistas, Cadex, Brostol, Elite Parcs), selon leurs propres seuils.">
            <Tableau rows={v.lignes} maxHeight={420} columns={[
              { key: 'statut', label: 'Statut', render: x => <Statut s={x.statut} />, sortValue: x => ({ DEPASSE: 3, CRITIQUE: 2, ALERTE: 1 }[x.statut]) },
              lienMateriel(periode), { key: 'type', label: 'Type' }, lienChantier(periode),
              { key: 'cycle', label: 'Depuis vidange', num: true, fmt: (x, row) => `${fmtNb(x)} / ${fmtNb(row.interval)} ${row.mode}` },
              { key: 'reste', label: 'Reste', num: true, fmt: fmtNb },
            ]} />
          </Section>
        </div>
        <div className="col-12 col-xl-6">
          <Section titre="📄 Papiers des véhicules" info="Jours restants avant échéance (négatif = expiré). Survolez pour la date.">
            <Tableau rows={p.lignes} maxHeight={420} columns={[
              { key: 'statut', label: 'Statut', render: x => <Statut s={x.statut} />, sortValue: x => (x.statut === 'DEPASSE' ? 2 : 1) },
              lienMateriel(periode), { key: 'type', label: 'Type' },
              ...PAPIERS.map(([k, label]) => ({ key: k, label, align: 'center', render: x => celluleEcheance(x.papiers[k]), sortValue: x => x.papiers[k]?.jours ?? 9999 })),
            ]} />
          </Section>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-xl-6">
          <Section titre="🔧 Réparations par matériel">
            <Tableau rows={r.parMateriel} total triInitial={{ key: 'montant', dir: 'desc' }} columns={[
              lienMateriel(periode), { key: 'type', label: 'Type' }, lienChantier(periode),
              { key: 'lignes', label: 'Lignes', num: true, total: true },
              { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
            ]} />
          </Section>
        </div>
        <div className="col-12 col-xl-6">
          <Section titre="🏪 Réparations par fournisseur">
            <Tableau rows={r.parFournisseur} total triInitial={{ key: 'montant', dir: 'desc' }} columns={[
              { key: 'fournisseur', label: 'Fournisseur', render: x => <Clic onClick={() => onListe('reparations', `Réparations — ${x.fournisseur}`, { fournisseur: x.fournisseur })}>{x.fournisseur}</Clic> },
              { key: 'materiels', label: 'Matériels', num: true },
              { key: 'lignes', label: 'Lignes', num: true, total: true },
              { key: 'montant', label: 'Montant (DH)', num: true, total: true, fmt: fmtMt },
            ]} />
          </Section>
        </div>
      </div>
    </>
  );
};

/* ─── Citernes ─────────────────────────────────────────────────────────────── */
const Jauge = ({ pct, bas }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}>
    <div style={{ flex: 1, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(pct || 0, 100)}%`, height: '100%', background: bas ? '#c62828' : pct < 40 ? '#e65100' : '#2e7d32' }} />
    </div>
    <span style={{ fontSize: '0.75rem', width: 36, textAlign: 'right' }}>{pct == null ? '—' : `${pct} %`}</span>
  </div>
);

export const Citernes = ({ data, onListe, periode }) => (
  <>
    <div className="row">
      <Stat label="Stock total" value={fmtL(data.stockTotal)} sub={`capacité ${fmtL(data.capaciteTotale)}`} onClick={() => onListe('citernes', 'Citernes actives')} />
      <Stat label="Citernes en stock bas" value={fmtNb(data.basses)} alert={data.basses > 0}
        onClick={data.basses ? () => onListe('citernes', 'Citernes en stock bas', { basses: 1 }) : null}
        info="Stock réel ≤ capacité minimum." />
      <Stat label="Livraisons (période)" value={fmtL(data.entrees)} onClick={data.entrees ? () => onListe('mouvements', 'Livraisons de gasoil', { type: 'ENTREE' }) : null} />
      <Stat label="Sorties (période)" value={fmtL(data.sorties)} onClick={data.sorties ? () => onListe('mouvements', 'Sorties de gasoil', { type: 'sorties' }) : null}
        info="Consommations des pointages matériel + sorties manuelles." />
    </div>
    <Section titre="⛽ Citernes" info="Autonomie = stock / consommation moyenne des 7 derniers jours.">
      <Tableau rows={data.citernes} total triInitial={{ key: 'autonomie', dir: 'asc' }} columns={[
        { key: 'nom', label: 'Citerne', render: c => <FicheLien type="citerne" id={c.id} periode={periode}>{c.nom}</FicheLien> },
        lienChantier(periode),
        { key: 'stock', label: 'Stock (L)', num: true, total: true, fmt: fmtNb },
        { key: 'remplissage', label: 'Remplissage', render: c => <Jauge pct={c.remplissage} bas={c.bas} />, sortValue: c => c.remplissage ?? -1 },
        { key: 'min', label: 'Min (L)', num: true, fmt: fmtNb },
        { key: 'consoJour', label: 'Conso / j (L)', num: true, fmt: dec },
        { key: 'autonomie', label: 'Autonomie', num: true, sortValue: c => c.autonomie ?? 9999,
          fmt: v => (v == null ? '—' : <span style={{ color: v < 2 ? '#c62828' : v < 5 ? '#e65100' : undefined, fontWeight: v < 5 ? 700 : undefined }}>{dec(v)} j</span>) },
        { key: 'entreesPeriode', label: 'Livré (L)', num: true, total: true, fmt: fmtNb },
        { key: 'sortiesPeriode', label: 'Sorti (L)', num: true, total: true, fmt: fmtNb },
      ]} />
    </Section>
  </>
);
